import { captureException } from "@sentry/react";
import { useConnection } from "@solana/wallet-adapter-react";
import type { PublicKey } from "@solana/web3.js";
import { NATIVE_SOL } from "@stepfinance/common";
import { getMarinadePoolProgram } from "@stepfinance/liquidity";
import { useEffect, useMemo, useState } from "react";

import { useEnrichedWallet } from "../contexts/enrichedWallet";
import { useMarkets } from "../contexts/market";
import { useRefreshContext } from "../contexts/refresh";
import { MARINADE_STATE_ADDRESS } from "../models/marinade";
import { useConnectedWallet } from "./wallet/useConnectedWallet";

export interface MarinadeState {
  programID: PublicKey;
  mSOLPricePerSOL: number;
  apy: number;
}

export const useMarinade = () => {
  const { connection } = useConnection();
  const { connected } = useEnrichedWallet();
  const wallet = useConnectedWallet();
  const { refreshing } = useRefreshContext();
  const { priceInUSD } = useMarkets();
  const [marinadeState, setMarinadeState] = useState<MarinadeState>();

  useEffect(() => setMarinadeState(undefined), [connected]);

  const marinadeProgram = useMemo(() => {
    if (!wallet) {
      return;
    }
    return getMarinadePoolProgram(connection);
  }, [wallet, connection]);

  useEffect(() => {
    const loadMarinadeState = async () => {
      if (!marinadeProgram) {
        return;
      }

      const solPrice = priceInUSD(NATIVE_SOL.toBase58());

      if (!solPrice || solPrice < 0) {
        return;
      }

      let apy = 0;
      try {
        const resp = await window.fetch("https://api.marinade.finance/msol/apy/1y");

        if (resp.ok) {
          const data = await resp.json();
          const marindadeAPY = data.value;
          if (marindadeAPY) {
            apy = marindadeAPY;
          }
        }
      } catch (err) {
        console.error("useMarinate Error:", err);
        captureException(err);
      }

      const state = await marinadeProgram.account.state.fetch(MARINADE_STATE_ADDRESS);
      const msolPrice = state.msolPrice.toNumber() / 0x1_0000_0000;

      setMarinadeState({
        programID: marinadeProgram.programId,
        mSOLPricePerSOL: msolPrice,
        apy,
      });
    };
    void loadMarinadeState();
  }, [marinadeProgram, refreshing, priceInUSD, connection]);

  return marinadeState;
};
